import { BaseQueryFn } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosResponseTransformer,
} from 'axios';
import { ServiceMediaUploads } from 'services/media-uploads';
import { isFileLike, ValueFileUploaderFile } from 'utils/file-uploader';
import * as dynamic from './dynamic';
import { equals, makeFilter } from './dynamic';
import { ConditionPartial, PatchPartial } from './types';

export const isServerDateString = <T>(value: T) => {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.?\d{0,7}$/gi.test(String(value));
};

export const axiosDateTransformer: AxiosResponseTransformer = (res) => {
  try {
    return JSON.parse(res, (key, value) => {
      if (typeof value === 'string' && isServerDateString(value)) {
        const _date = Date.parse(value);
        if (_date) {
          return `${value.split('.')[0]}.000Z`;
        }
      }
      return value;
    });
  } catch (e) {
    return res;
  }
};

export const BASE_URL = process.env.REACT_APP_API_SERVER;

export const apiApp = axios.create({
  baseURL: `${BASE_URL}`,
  transformResponse: [axiosDateTransformer],
});
export const apiFree = axios.create({
  baseURL: `${BASE_URL}`,
  transformResponse: [axiosDateTransformer],
});

export interface DynamicParams {
  select?: string;
  filter?: string;
  orderBy?: string;
  take?: number;
  skip?: number;
  count?: boolean;
}

export type DynamicResult<T extends any, P extends DynamicParams = {}> = P extends {
  count: boolean;
}
  ? { value: T[]; count: number }
  : { value: T[] };

export const axiosBaseGetQuery =
  (): BaseQueryFn<Pick<AxiosRequestConfig, 'params' | 'url'>, unknown, AxiosError | Error> =>
  async ({ url, params }) => {
    try {
      const result = await apiApp.get(url || '', { params });
      return { data: result.data };
    } catch (axiosError) {
      let err = axiosError as AxiosError;
      return {
        error: err,
      };
    }
  };
export const apiRtk = createApi({
  reducerPath: 'apiRtk',
  baseQuery: axiosBaseGetQuery(),
  tagTypes: [
    'Customers',
    'Labels',
    'Languages',
    'Projects',
    'ProjectTypes',
    'ProjectStatuses',
    'ProjectAlerts',
    'ProjectPriceLists',
    'ProjectWikiItems',
    'ProjectTimeTracking',
    'UserCmsProfilePermissions',
    'UserCrmProfiles',
    'TimeTracking',
    'Regions',
    'NamePrefixesEng',
    'MemberTypes',
    'WorkPlaces',
    'WorkPlaceTypes',
    'Cities',
    'PaymentTypes',
    'Nationalities',
    'NamePrefixes',
    'ExternalChannels',
    'UserPharmacistProfiles',
    'UserPharmacistProfilePayments',
    'Leads',
    'Parts',
    'CommonPageItems',
    'CommonPages',
    'SiteParameters',
    'SiteParameterDetails',
    'SiteParameterSocialMedias',
    'GlobalParameters',
  ],
  endpoints: () => ({}),
});

export const parseErrorData = <T = string>(error: AxiosError<T> | Partial<Error>) => {
  if (!error) {
    return new Error('error');
  }
  if ('isAxiosError' in error) {
    const errorData = error.response?.data;

    if (!errorData) {
      return new Error('error');
    }

    if (typeof errorData === 'string') {
      return new Error(errorData);
    }
    return { message: 'error', ...errorData };
  }
  return new Error(error.message);
};

export const isRejectedMutation = <T>(mutationResult: any): mutationResult is { error: T } => {
  return Boolean(mutationResult && mutationResult.error);
};
export const isFulfilledMutation = <T>(mutationResult: any): mutationResult is { data: T } => {
  return Boolean(
    mutationResult && mutationResult.hasOwnProperty && mutationResult.hasOwnProperty('data'),
  );
};

export const transformResponseDynamic = <T>(data: { value: T[] }) => {
  return data.value;
};
export const transformResponseDynamicItemMaybe = <T>(data: { value: T[] }) => {
  return data.value[0];
};
export const transformResponseDynamicItem = <T>(data: { value: T[] }) => {
  const item = data.value[0];
  if (!item) {
    throw new Error('record-not-found');
  }
  return item;
};

export const calcPaginationSkip = ({ page, take }: { take: number; page: number }) => {
  return take * (page - 1);
};
export const calcPaginationState = ({
  take,
  page,
  count,
}: {
  take: number;
  count: number;
  page: number;
}) => {
  const skip = calcPaginationSkip({ take, page });
  const pages = Math.ceil(count / take);
  const isLastPage = pages === page;
  const isFirstPage = page === 1;
  return {
    take,
    page,
    count,
    pages,
    skip,
    isFirstPage,
    isLastPage,
  };
};

const prepareFieldWithID = (value: any, key: any) => {
  return value === '' && String(key).endsWith('ID') ? null : value;
};

export const prepareRequestData = <T extends { [x: string]: any | null } = {}>(data: T) => {
  const keys = Object.keys(data) as (keyof T)[];
  return keys.reduce((acc, key) => {
    const value = acc[key];
    acc[key] = prepareFieldWithID(value, key);
    return acc;
  }, data);
};

interface DynamicModel {
  id: string;
}

interface DynamicServiceOptions<M> {
  engine?: AxiosInstance;
  getAll: string;
  post: string;
  patch: (data: PatchPartial<M, keyof M>) => string;
  delete: (data: PatchPartial<M, keyof M>) => string;
}

export class DynamicService<M = DynamicModel> {
  public engine: AxiosInstance = apiApp;
  public urlGetAll: string;
  public urlPost: string;
  public urlPatch: (data: PatchPartial<M, keyof M>) => string;
  public urlDelete: (data: PatchPartial<M, keyof M>) => string;
  public mainField: string;

  constructor(
    options: DynamicServiceOptions<M> & ConditionPartial<M, DynamicModel, { mainField: keyof M }>,
  ) {
    const { getAll, patch, post, engine = this.engine, mainField } = options;

    this.mainField = String(mainField || 'id');

    this.engine = engine;

    this.urlGetAll = getAll;
    this.urlPatch = patch;
    this.urlPost = post;
    this.urlDelete = options.delete;

    this.getAllDynamic = this.getAllDynamic.bind(this);
    this.getDynamic = this.getDynamic.bind(this);
    this.patch = this.patch.bind(this);
    this.post = this.post.bind(this);
    this.delete = this.delete.bind(this);
  }

  async getAllDynamic<Model = M, Params extends DynamicParams = DynamicParams>(params?: Params) {
    return this.engine.get<DynamicResult<Model, Params>>(this.urlGetAll, { params });
  }

  async getDynamic<Model = M, Params extends DynamicParams = DynamicParams>(
    id: string,
    params?: Params,
  ) {
    const result = await this.getAllDynamic<Model>({
      ...params,
      filter: dynamic
        .mergeFilters(
          dynamic.makeFilter<{ id: string }>(this.mainField as any, id, dynamic.equals),
          params?.filter,
        )
        .join('&&'),
      take: 1,
    });
    const data = result.data.value[0];
    if (!data) {
      throw new Error('record-not-found');
    }
    return { ...result, data };
  }

  async patch(data: Partial<M>) {
    return this.engine.patch(this.urlPatch(data as any), { ...data, [this.mainField]: undefined });
  }

  async post(data: M) {
    return this.engine.post<M>(this.urlPost, data);
  }

  async delete(data: Partial<M>) {
    return this.engine.delete(this.urlDelete(data as any));
  }
}
export const decoratorWithFiles = <
  T extends {
    getAllDynamic: (params?: Partial<DynamicParams>) => Promise<AxiosResponse<DynamicResult<M>>>;
  },
  M extends Record<string, any>,
>(
  mainField: keyof M,
  ...fileFields: (keyof M)[]
) => {
  return (target: T, propertyName: string, propertyDescriptor: PropertyDescriptor) => {
    const origin = propertyDescriptor.value;

    propertyDescriptor.value = async function (formData: M) {
      const service = this as T;
      let oldData = {} as M;

      const shouldLoadData =
        formData[mainField] &&
        Object.keys(formData).some((formDataKey) =>
          fileFields.some((fileField) => fileField === formDataKey),
        );

      if (shouldLoadData) {
        const {
          data: { value },
        } = await service.getAllDynamic({
          filter: makeFilter<any>(String(mainField), formData[mainField], equals),
          select: [...fileFields].join(','),
          take: 1,
        });

        oldData = value[0] || {};
      }

      let entriesToUpload: [keyof M, ValueFileUploaderFile][] = [];
      let entriesToDelete: [keyof M, string][] = [];

      fileFields.forEach((key) => {
        if (isFileLike(formData[key])) {
          entriesToUpload.push([key, formData[key]]);
        }
        if (oldData[key] && typeof oldData[key] === 'string' && oldData[key] !== formData[key]) {
          entriesToDelete.push([key, oldData[key]]);
        }
      });

      const entriesDeleted = await Promise.all(
        entriesToDelete.map(async ([key, value]) => {
          await ServiceMediaUploads.remove({ filePath: value });
          return [key, ''];
        }),
      );
      const entriesUploaded = await Promise.all(
        entriesToUpload.map(async ([key, value]) => {
          const {
            data: { filePath },
          } = await ServiceMediaUploads.uploadFile(value);
          return [key, filePath];
        }),
      );
      const updatedData = [...entriesDeleted, ...entriesUploaded].reduce(
        (acc, [key, value]) => {
          // @ts-ignore
          acc[key] = value;
          return acc;
        },
        { ...formData },
      );

      return origin.call(this, updatedData);
    };
  };
};

export interface ValueHtmlContentUploader {
  name: string;
  value: string;
}

export const decoratorWithHtmlContent = <
  T extends {
    getAllDynamic: (params?: Partial<DynamicParams>) => Promise<AxiosResponse<DynamicResult<M>>>;
  },
  M extends Record<string, any>,
>(
  mainField: keyof M,
  ...fileFields: (keyof M)[]
) => {
  return (target: T, propertyName: string, propertyDescriptor: PropertyDescriptor) => {
    const origin = propertyDescriptor.value;

    propertyDescriptor.value = async function (formData: M) {
      const service = this as T;
      let oldData = {} as M;

      const shouldLoadData =
        formData[mainField] &&
        Object.keys(formData).some((formDataKey) =>
          fileFields.some((fileField) => fileField === formDataKey),
        );

      if (shouldLoadData) {
        const {
          data: { value },
        } = await service.getAllDynamic({
          filter: makeFilter<any>(String(mainField), formData[mainField], equals),
          select: [...fileFields].join(','),
          take: 1,
        });

        oldData = value[0] || {};
      }

      let entriesToUpload: [keyof M, ValueHtmlContentUploader][] = [];

      fileFields.forEach((key) => {
        if (formData[key] && oldData[key] !== formData[key]) {
          entriesToUpload.push([key, { name: formData['id'] || '', value: formData[key] }]);
        }
      });
      const entriesUploaded = await Promise.all(
        entriesToUpload.map(async ([key, value]) => {
          const {
            data: { filePath },
          } = await ServiceMediaUploads.uploadHtml(value);
          return [key, filePath];
        }),
      );

      const updatedData = [...entriesUploaded].reduce(
        (acc, [key, value]) => {
          // @ts-ignore
          acc[key] = value;
          return acc;
        },
        { ...formData },
      );

      return origin.call(this, updatedData);
    };
  };
};

export const decoratorRank = <
  M extends Record<string, any>,
  T extends {
    getAllDynamic: (
      params: Pick<DynamicParams, 'orderBy'>,
    ) => Promise<AxiosResponse<DynamicResult<M>>>;
  },
>(
  rankField: keyof M,
) => {
  return function (target: T, memberName: string, propertyDescriptor: PropertyDescriptor) {
    const origin = propertyDescriptor.value;

    propertyDescriptor.value = async function (formData: any) {
      const {
        data: { value },
        // @ts-ignore
      } = await this.getAllDynamic({
        take: 1,
        select: rankField,
        orderBy: `${String(rankField)} desc `,
      });
      const rankValue = value[0] || { [rankField]: 0 };
      return origin.call(this, { ...formData, [rankField]: rankValue[rankField] + 1 });
    };
  };
};

interface MoveRowsOptions<T extends Record<string, any>> {
  mainField: keyof T;
  moveField: keyof T;
  requestPatch: (data: Partial<T>) => void;
  newRows: Partial<T>[];
  oldRows: Partial<T>[];
}
export const behaviourMoveRows = async <T extends Record<string, any>>(
  options: MoveRowsOptions<T>,
) => {
  const { newRows, oldRows, requestPatch } = options;
  return Promise.all(
    newRows.map((oldRecord, i) => {
      return requestPatch({
        [options.mainField]: oldRecord[options.mainField],
        [options.moveField]: oldRows[i][options.moveField],
      } as Partial<T>);
    }),
  );
};
